import { GRACE_WINDOW_HOURS, type IssueDetail } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ApiError, q, useIssueAction } from "@/lib/api";
import { CheckCircle2, ChevronRight, CircleDashed, FileSearch, FlaskConical, GitCommitHorizontal, Inbox, ShieldCheck, ShieldOff, XCircle } from "lucide-react";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";
import { GraceRing, graceColor, useNow } from "@/components/domain/countdown";
import { AgentLink, PageHeader, SignalChip, TimeAgo } from "@/components/domain/misc";
import { Delta } from "@/components/domain/score";
import { CoverageChip, IssueStateBadge, SeverityPill } from "@/components/domain/status";
import { ErrorPanel, Page } from "@/components/layout/page";
import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { EmptyState, Meter, Skeleton } from "@/components/ui/misc";
import { ActivityLog } from "@/features/issues/activity";
import { DeclareFixDialog, DismissDialog } from "@/features/issues/dialogs";
import { FixCard } from "@/features/issues/fix-card";
import { FIX_ORDER, FIXABLE_STATES, graceWindowLabel } from "@/features/issues/lib";
import { LifecycleStepper } from "@/features/issues/lifecycle";
import { MiniMarkdown } from "@/features/issues/markdown";
import { RepresentativeTrace } from "@/features/issues/trace-evidence";
import { fmtDateTime, fmtInt, fmtMoney, fmtRemaining } from "@/lib/format";
import { usePackName } from "@/lib/packs";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/issues/$issueId")({
  component: IssuePage,
});

function IssuePage() {
  const { issueId } = Route.useParams();
  const query = useQuery(q.issue(issueId));

  const breadcrumb = (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1">
      <Link to="/issues" className="hover:text-fg">
        Issues
      </Link>
      <ChevronRight className="size-3" aria-hidden />
      <span className="font-mono text-fg-2" aria-current="page">
        {issueId}
      </span>
    </nav>
  );

  if (query.error) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    return (
      <Page>
        <PageHeader title={issueId} breadcrumb={breadcrumb} />
        {notFound ? (
          <Card>
            <EmptyState
              icon={<FileSearch />}
              title={`Issue ${issueId} not found`}
              description="It may have been merged into another issue, or the link is mistyped."
              action={
                <Button asChild variant="outline" size="sm" className="mt-2">
                  <Link to="/issues">Back to issues</Link>
                </Button>
              }
            />
          </Card>
        ) : (
          <ErrorPanel error={query.error} reset={() => void query.refetch()} />
        )}
      </Page>
    );
  }

  if (!query.data) return <IssueSkeleton breadcrumb={breadcrumb} />;
  return <IssueView issue={query.data} breadcrumb={breadcrumb} />;
}

function IssueSkeleton({ breadcrumb }: { breadcrumb: ReactNode }) {
  return (
    <Page>
      <div className="mb-1.5 text-xs text-fg-3">{breadcrumb}</div>
      <Skeleton className="h-28 w-full" />
      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="flex flex-col gap-4">
          <Skeleton className="h-48" />
          <Skeleton className="h-72" />
        </div>
        <div className="flex flex-col gap-4">
          <Skeleton className="h-64" />
          <Skeleton className="h-40" />
        </div>
      </div>
    </Page>
  );
}

function IssueView({ issue, breadcrumb }: { issue: IssueDetail; breadcrumb: ReactNode }) {
  const fixes = [...issue.fixes].sort((a, b) => FIX_ORDER[a.kind] - FIX_ORDER[b.kind]);
  const canDeclareFix = FIXABLE_STATES.has(issue.state);
  const packName = usePackName();

  return (
    <Page>
      <div className="mb-2 text-xs text-fg-3">{breadcrumb}</div>
      <IssueHeader issue={issue} canDeclareFix={canDeclareFix} />
      {issue.state === "detected" ? <TriageBar issue={issue} /> : null}

      <div className="mt-4 grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        {/* LEFT */}
        <div className="flex min-w-0 flex-col gap-4">
          <Card>
            <CardHeader title="What happened" description={[plural(issue.affectedSessions, "session"), issue.affectedEndUsers ? plural(issue.affectedEndUsers, "end user") : null, `${packName(issue.pack)} pack`].filter(Boolean).join(" · ")} />
            <CardBody className="flex flex-col gap-4">
              <MiniMarkdown source={issue.summary} />
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-fg-3">Signals</span>
                {issue.signalTypes.map((s) => (
                  <SignalChip key={s} type={s} severity={issue.severity} />
                ))}
              </div>
              <section aria-labelledby="root-cause" className="rounded-md border border-dashed bg-surface-2/50 px-3.5 py-3">
                <h3 id="root-cause" className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-fg-3">
                  Root-cause hypothesis
                </h3>
                <MiniMarkdown source={issue.rootCause} />
              </section>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Representative traces"
              description={
                issue.representativeTraceIds.length
                  ? `${Math.min(5, issue.representativeTraceIds.length, Math.max(issue.affectedSessions, 1))} of ${plural(issue.affectedSessions, "affected session")} · flagged turn highlighted`
                  : undefined
              }
              actions={
                <Button asChild variant="ghost" size="sm">
                  <Link to="/traces">All traces</Link>
                </Button>
              }
            />
            <CardBody>
              {issue.representativeTraceIds.length ? (
                <TraceList ids={issue.representativeTraceIds.slice(0, 5)} issueId={issue.id} />
              ) : (
                <p className="text-[13px] text-fg-3">No representative traces retained for this issue.</p>
              )}
            </CardBody>
          </Card>

          <section aria-labelledby="fixes-title" className="flex flex-col gap-3">
            <div className="flex items-end justify-between gap-3">
              <div>
                <h2 id="fixes-title" className="text-[13px] font-semibold">
                  Suggested fixes
                </h2>
                <p className="mt-0.5 text-xs text-fg-3">A control enforced by the agent platform mitigates immediately and keeps the scenario covered; a verified code or prompt fix resolves it.</p>
              </div>
            </div>
            {fixes.length ? (
              fixes.map((f, i) => <FixCard key={f.id} issue={issue} fix={f} letter={String.fromCharCode(65 + i)} recommended={f.kind === "control" && f.status !== "applied" && ["detected", "grace", "regressed", "excluded"].includes(issue.state)} />)
            ) : (
              <Card>
                <EmptyState icon={<Inbox />} title="No suggested fixes yet" description="Fix suggestions are generated after triage." />
              </Card>
            )}
          </section>

          <Card>
            <CardHeader title="Activity" description="Lifecycle events, CI annotations and team discussion" />
            <CardBody>
              <ActivityLog issue={issue} />
            </CardBody>
          </Card>
        </div>

        {/* RIGHT */}
        <aside className="flex flex-col gap-4 lg:sticky lg:top-4" aria-label="Lifecycle and coverage">
          <Card>
            <CardHeader title="Lifecycle" />
            <CardBody>
              <LifecycleStepper issue={issue} />
            </CardBody>
          </Card>
          <CoverageImpactCard issue={issue} />
          <VerificationCard issue={issue} canDeclareFix={canDeclareFix} />
        </aside>
      </div>
    </Page>
  );
}

function TraceList({ ids, issueId }: { ids: string[]; issueId: string }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? ids : ids.slice(0, 3);
  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-col gap-2">
        {shown.map((t) => (
          <RepresentativeTrace key={t} traceId={t} issueId={issueId} />
        ))}
      </ul>
      {ids.length > 3 ? (
        <Button variant="ghost" size="sm" className="self-start" onClick={() => setExpanded((e) => !e)} aria-expanded={expanded}>
          {expanded ? "Show fewer" : `Show ${ids.length - 3} more`}
        </Button>
      ) : null}
    </div>
  );
}

// ─── Header ──────────────────────────────────────────────────────────────────

function IssueHeader({ issue, canDeclareFix }: { issue: IssueDetail; canDeclareFix: boolean }) {
  const who: ReactNode[] = [
    <AgentLink key="a" id={issue.agentId} name={issue.agentName} />,
    <span key="f">
      first seen <TimeAgo iso={issue.firstSeen} />
    </span>,
    <span key="l">
      last seen <TimeAgo iso={issue.lastSeen} />
    </span>,
  ];
  const impact: ReactNode[] = [
    <span key="s" className="tabular">
      <span className="text-fg">{fmtInt(issue.affectedSessions)}</span> {issue.affectedSessions === 1 ? "session" : "sessions"}
    </span>,
    ...(issue.affectedEndUsers
      ? [
          <span key="c" className="tabular">
            <span className="text-fg">{fmtInt(issue.affectedEndUsers)}</span> {issue.affectedEndUsers === 1 ? "end user" : "end users"}
          </span>,
        ]
      : []),
    <span key="e" className="tabular">
      <span className="text-fg">{fmtMoney(issue.exposure)}</span> exposure
    </span>,
    <span key="v">
      {issue.versions.length > 1 ? "versions" : "version"} <span className="font-mono text-[12px] text-fg">{issue.versions.join(", ") || "—"}</span>
    </span>,
  ];

  return (
    <Card className="px-5 py-4">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[12.5px] text-fg-3">{issue.id}</span>
            <SeverityPill severity={issue.severity} />
            <IssueStateBadge state={issue.state} />
          </div>
          <h1 className="mt-1.5 text-lg font-semibold leading-snug tracking-tight text-balance">{issue.title}</h1>
          <MetaLine items={who} className="mt-2" />
          <MetaLine items={impact} className="mt-1" />
        </div>
        <div className="flex shrink-0 flex-col items-end gap-3">
          <CoverageHero issue={issue} />
          {canDeclareFix && issue.state !== "detected" ? (
            <DeclareFixDialog
              issue={issue}
              trigger={
                <Button variant="outline" size="sm">
                  <GitCommitHorizontal /> Declare fix version
                </Button>
              }
            />
          ) : null}
        </div>
      </div>
    </Card>
  );
}

function MetaLine({ items, className }: { items: ReactNode[]; className?: string }) {
  return (
    <div className={cn("flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-fg-3", className)}>
      {items.map((m, i) => (
        <span key={i} className="inline-flex items-center gap-2">
          {i > 0 ? <span aria-hidden>·</span> : null}
          {m}
        </span>
      ))}
    </div>
  );
}

const plural = (n: number, word: string) => `${fmtInt(n)} ${word}${n === 1 ? "" : "s"}`;

function CoverageHero({ issue }: { issue: IssueDetail }) {
  const now = useNow();
  const critical = issue.severity === "critical";
  let ring: ReactNode;
  let headline: ReactNode;
  let sub: ReactNode;
  let ink = "var(--text)";

  const timed = issue.graceStartedAt && issue.graceDeadline && ["grace", "regressed", "verifying"].includes(issue.state);
  if (timed) {
    const start = Date.parse(issue.graceStartedAt!);
    const end = Date.parse(issue.graceDeadline!);
    const left = end - now;
    const frac = Math.max(0, Math.min(1, left / Math.max(1, end - start)));
    ink = graceColor(frac);
    ring = <GraceRing start={issue.graceStartedAt!} deadline={issue.graceDeadline!} size={44} stroke={4} />;
    const prefix = issue.state === "verifying" ? "Covered · verifying" : critical ? "Covered at 50%" : "Covered";
    headline = left <= 0 ? `${prefix} · deadline passed` : `${prefix} · ${fmtRemaining(left)} left`;
    sub = `${issue.state === "regressed" ? "Halved grace window" : "Grace"} ends ${fmtDateTime(issue.graceDeadline!)}`;
  } else {
    const iconCls = "size-[44px] rounded-full border p-2.5";
    switch (issue.state) {
      case "detected":
        ring = <CircleDashed className={cn(iconCls, "border-dashed text-fg-3")} aria-hidden />;
        headline = "Covered · awaiting triage";
        sub = `${graceWindowLabel(issue.severity)} grace window starts on confirmation`;
        break;
      case "mitigated":
        ring = <ShieldCheck className={cn(iconCls, "text-info-text")} aria-hidden />;
        headline = "Covered · mitigated";
        sub = "Stays covered while the platform keeps enforcing the control";
        break;
      case "verifying":
        ring = <FlaskConical className={cn(iconCls, "text-violet-text")} aria-hidden />;
        headline = "Covered · verifying";
        sub = "Replay, probes and production soak in progress";
        break;
      case "resolved":
        ring = <CheckCircle2 className={cn(iconCls, "text-good-text")} aria-hidden />;
        headline = "Covered · resolved";
        sub = "Verified fix — scenario promoted to covered";
        break;
      case "excluded":
        ring = <ShieldOff className={cn(iconCls, "text-critical-text")} aria-hidden />;
        headline = issue.coverageState === "graduated" ? "Graduated coverage" : "Not covered";
        ink = "var(--critical-text)";
        sub = "Grace expired — declare a verified fix to restore coverage";
        break;
      default:
        ring = <CircleDashed className={cn(iconCls, "text-fg-3")} aria-hidden />;
        headline = "Coverage pending";
        sub = null;
    }
  }

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-surface-2/60 py-2 pl-2.5 pr-4">
      {ring}
      <div className="min-w-0">
        <div className="text-[15px] font-semibold leading-tight tabular" style={{ color: ink }}>
          {headline}
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs text-fg-3">
          <CoverageChip state={issue.coverageState} />
          {sub ? <span>{sub}</span> : null}
        </div>
      </div>
    </div>
  );
}

function TriageBar({ issue }: { issue: IssueDetail }) {
  const action = useIssueAction(issue.id);
  const hours = GRACE_WINDOW_HOURS[issue.severity];
  return (
    <div role="region" aria-label="Triage" className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-border-strong bg-surface px-4 py-3">
      <CircleDashed className="size-4 text-fg-3" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium text-fg">Awaiting triage</p>
        <p className="text-xs text-fg-3">
          Confirming notifies the owners and starts the {hours < 72 ? `${hours}-hour` : `${hours / 24}-day`} grace window — the scenario stays covered while you fix it. Dismiss if this is noise.
        </p>
      </div>
      <DismissDialog
        issue={issue}
        trigger={
          <Button variant="ghost" size="sm">
            <XCircle /> Dismiss
          </Button>
        }
      />
      <Button
        size="sm"
        disabled={action.isPending}
        onClick={() =>
          action.mutate(
            { action: "confirm" },
            { onSuccess: (d) => toast.success(`${issue.id} confirmed`, { description: d.graceDeadline ? `Grace window running until ${fmtDateTime(d.graceDeadline)}.` : undefined }) },
          )
        }
      >
        <CheckCircle2 /> {action.isPending ? "Confirming…" : "Confirm & start grace"}
      </Button>
    </div>
  );
}

// ─── Right column cards ──────────────────────────────────────────────────────

function CoverageImpactCard({ issue }: { issue: IssueDetail }) {
  // The grace window applies to every policy covering this agent-scenario.
  const scenariosQ = useQuery(q.scenarios({ agentId: issue.agentId }));
  const policies = [...new Map((scenariosQ.data ?? []).filter((s) => s.key === issue.scenarioKey).map((s) => [s.policyId, s.policyName])).values()];
  return (
    <Card>
      <CardHeader title="Coverage impact" />
      <CardBody className="flex flex-col gap-3.5">
        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-medium uppercase tracking-wide text-fg-3">Scenario</span>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="break-all font-mono text-[12px] text-fg">{issue.scenarioKey}</span>
            <CoverageChip state={issue.coverageState} />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-medium uppercase tracking-wide text-fg-3">{policies.length === 1 ? "Policy affected" : "Policies affected"}</span>
          {scenariosQ.isPending ? (
            <Skeleton className="h-5 w-40" />
          ) : policies.length ? (
            <ul className="flex flex-wrap gap-1.5">
              {policies.map((name) => (
                <li key={name}>
                  <Badge tone="neutral">{name}</Badge>
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-xs text-fg-3">No active policy covers this scenario</span>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-medium uppercase tracking-wide text-fg-3">
            {issue.state === "resolved" ? "Outcome" : issue.graceDeadline && issue.state !== "excluded" ? `If unresolved by ${fmtDateTime(issue.graceDeadline)}` : "If unresolved"}
          </span>
          <p className="text-[13px] leading-relaxed text-fg-2">{issue.ifUnresolved}</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <ArsImpact label="ARS impact now" value={issue.arsImpactNow} />
          <ArsImpact label="On resolve" value={issue.arsImpactOnResolve} />
        </div>
      </CardBody>
    </Card>
  );
}

function ArsImpact({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border bg-surface-2/60 px-3 py-2">
      <div className="text-[11px] text-fg-3">{label}</div>
      <div className="mt-1 text-lg font-semibold leading-none tabular">
        {value === 0 ? <span className="text-fg-3">±0</span> : <Delta value={value} className="text-lg font-semibold" />}
      </div>
    </div>
  );
}

const REPLAY: Record<IssueDetail["verification"]["replay"], { tone: BadgeTone; label: string }> = {
  not_started: { tone: "neutral", label: "Not started" },
  running: { tone: "info", label: "Running" },
  passed: { tone: "good", label: "Passed" },
  failed: { tone: "critical", label: "Failed" },
  unavailable: { tone: "muted", label: "Unavailable" },
};

function VerificationCard({ issue, canDeclareFix }: { issue: IssueDetail; canDeclareFix: boolean }) {
  const v = issue.verification;
  const replay = REPLAY[v.replay];
  const probesDone = v.probes.total > 0 && v.probes.passed === v.probes.total;
  const soakDone = v.soak.target > 0 && v.soak.sessions >= v.soak.target;
  return (
    <Card>
      <CardHeader title="Verification" description="Fixed is proven by evidence, not by declaration" />
      <CardBody className="flex flex-col gap-3.5">
        <Row label="Fix version">
          {v.fixVersion ? (
            <span className="font-mono text-[12px] text-fg">{v.fixVersion}</span>
          ) : canDeclareFix && issue.state !== "detected" ? (
            <DeclareFixDialog
              issue={issue}
              trigger={
                <Button variant="link" size="sm" className="h-auto text-xs">
                  Declare…
                </Button>
              }
            />
          ) : (
            <span className="text-xs text-fg-3">Not declared</span>
          )}
        </Row>
        <Row label="Offline replay">
          <Badge tone={replay.tone}>
            {v.replay === "passed" ? <CheckCircle2 /> : v.replay === "failed" ? <XCircle /> : v.replay === "running" ? <FlaskConical /> : null}
            {replay.label}
          </Badge>
        </Row>
        <MeterRow label="Probe suite" value={v.probes.passed} max={v.probes.total} done={probesDone} empty="No probes generated" unit="passed" />
        <MeterRow label="Production soak" value={v.soak.sessions} max={v.soak.target} done={soakDone} empty="—" unit="sessions" />
      </CardBody>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 text-[13px]">
      <span className="text-fg-3">{label}</span>
      {children}
    </div>
  );
}

function MeterRow({ label, value, max, done, empty, unit }: { label: string; value: number; max: number; done: boolean; empty: string; unit: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-3 text-[13px]">
        <span className="text-fg-3">{label}</span>
        {max > 0 ? (
          <span className="inline-flex items-center gap-1 text-xs tabular text-fg-2">
            {done ? <CheckCircle2 className="size-3.5 text-good-text" aria-label="complete" /> : null}
            <span>
              <span className="font-medium text-fg">{fmtInt(value)}</span>/{fmtInt(max)} {unit}
            </span>
          </span>
        ) : (
          <span className="text-xs text-fg-3">{empty}</span>
        )}
      </div>
      {max > 0 ? <Meter value={value} max={max} tone={done ? "var(--good)" : "var(--accent)"} label={`${label}: ${value} of ${max} ${unit}`} /> : null}
    </div>
  );
}
